import { Button } from '@mui/material';
import { IAPIError_postCancelOrder, IOrderStatus, IPreorderStatus } from 'my-poppies-b2b-types';
import { useCallback, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { sleep } from 'sleep-lightweight';

import { useApi } from '@/api/useApi';
import { colors } from '@/colors';
import { Container } from '@/components/Container';
import { DataContext } from '@/contexts/DataContext';
import { GlobalStateContext } from '@/contexts/GlobalStateContext';
import { OrderContext } from '@/contexts/OrderContext';
import { useAppPath } from '@/hooks/useAppPath';
import { useAppPush } from '@/hooks/useAppPush';
import useTranslation from '@/hooks/useTranslation';

interface IOrderEditButtonProps {
  orderId: string;
  status: IOrderStatus | IPreorderStatus;
}

export const OrderCancelButton = ({ orderId, status }: IOrderEditButtonProps) => {
  const t = useTranslation();
  const { callApi } = useApi();
  const { pathname } = useAppPath();
  const { push } = useAppPush();
  const [isSubmitting, setSubmitting] = useState(false);
  const { setDimmerState } = useContext(GlobalStateContext);
  const { fetchOrders } = useContext(OrderContext);
  const { updateCount } = useContext(DataContext);

  const submitCancel = useCallback(async () => {
    try {
      setSubmitting(true);
      setDimmerState('SHOW_WITH_SPINNER');

      const response = await callApi('post', '/cancel', { afterSuccessSleep: 2000, body: { id: orderId } }, () => {});

      if ((response as IAPIError_postCancelOrder).errorCode) {
        throw new Error('Api Error');
      } else {
        const wait = 2000;
        await fetchOrders([status]);
        await updateCount();
        setDimmerState('SHOW_WITHOUT_SPINNER');
        toast.success(t('order.cancel.success'), { duration: wait + 1000 });
        if (pathname !== '/') {
          push('/');
        }
        await sleep(wait);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      toast.error(t('order.cancel.error'));
    } finally {
      setSubmitting(false);
      setDimmerState('HIDDEN');
    }
  }, [callApi, fetchOrders, orderId, pathname, push, setDimmerState, status, t, updateCount]);

  return (
    <Container
      debugColor={'red'}
      sx={{ display: 'flex', alignItems: 'flex-start', flexGrow: 0, width: 'unset', margin: 'unset' }}
    >
      <Button
        disabled={isSubmitting}
        onClick={async () => {
          const confirmed = window.confirm(t('order.cancel.confirm', undefined, undefined, { orderId }));
          if (confirmed) {
            await submitCancel();
          } else {
            // do nothing
          }
        }}
        sx={{ backgroundColor: colors.error, fontSize: 'min(12px, 3dvw)' }}
      >
        {t('order.cancel')}
      </Button>
    </Container>
  );
};
